import _ from "lodash";
import React from "react";

import classNames from "classnames";
import { connect } from "react-redux";
import { useI18n } from "Hooks";

import {
  Tooltip,
  IconButton,
  Button,
  EmptyState,
  Avatar,
} from "@toddle-edu/ds-web";
import { MinusCircleOutlined, ChevronUpOutlined } from "@toddle-edu/ds-icons";
import StudentAlerts from "AppComponents/StudentAlerts";
import {
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
} from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";

import {
  SUB_TEXT_LENGTH,
  getFormattedNameForUserType,
} from "MessagingV2/modules/Utils";
import { isIdEqual } from "Utils";

import classes from "./ViewMembers.scss";
import { useMetadata } from "../MetadataProvider";
import { getSubText } from "../../module/utils";

const userTypeLabelsPlural = {
  [STUDENT]: "common:student_plural",
  [PARENT]: "common:family_member_plural",
  [STAFF]: "common:staff_members_plural",
};

const UserTypeCard = ({
  onRemoveClick,
  userId,
  mainText,
  subText,
  profileImage,
  testId,
  isStudentType,
  alertTypes,
}) => {
  const componentTestId = testId ? `${testId}-userTypeCard` : undefined;
  const { t } = useI18n();
  const mainTextClasses = classNames(classes.title, "text-body");
  const subTextClasses = classNames(classes.userSubTitle, "text-body-s");

  return (
    <div className={classes.userTypeCardContainer}>
      <Avatar
        dsVersion="2.0"
        id={userId}
        name={mainText}
        profileImage={profileImage}
        size="medium"
        shape="square"
        className={classes.userAvatar}
      />
      <div className={classes.userInfoContainer}>
        <div className={classes.titleContainer}>
          <div className={mainTextClasses}>{mainText}</div>
          {isStudentType ? (
            <StudentAlerts studentId={userId} alertTypes={alertTypes} />
          ) : null}
        </div>
        {subText ? (
          <ConditionalWrapper
            wrapper={<Tooltip tooltip={subText} />}
            condition={_.size(subText) > SUB_TEXT_LENGTH}
            type={"wrapperAsElement"}
          >
            <div className={subTextClasses}>
              {_.truncate(subText, { length: SUB_TEXT_LENGTH })}
            </div>
          </ConditionalWrapper>
        ) : null}
      </div>
      <div className={classes.iconButtonContainer}>
        <Tooltip
          tooltip={t("common:remove_with_label", {
            label: t("common:recipient"),
          })}
          placement="top"
        >
          <IconButton
            dsVersion="2.0"
            icon={<MinusCircleOutlined />}
            variant="neutral"
            type="plain"
            size="medium"
            onClick={onRemoveClick}
            className={classes.removeIcon}
            testId={`${componentTestId}-${userId}`}
          />
        </Tooltip>
      </div>
    </div>
  );
};

const UserTypeCards = ({
  users,
  onRemoveUsers,
  defaultStudentNameSortPreference,
  loggedInUserId,
  testId,
}) => {
  const { t } = useI18n(["common"]);
  const { showStudentId } = useMetadata();

  const componentTestId = testId ? `${testId}-userTypeCards` : undefined;
  return _.map(users, user => {
    const isCurrentLoggedInUser = isIdEqual(loggedInUserId, user.id);

    const handleRemoveUser = () => onRemoveUsers([user]);

    const { id, profileImage, type, alertTypes = [] } = user;

    let name = getFormattedNameForUserType({
      user,
      orderBy: defaultStudentNameSortPreference,
    });

    if (isCurrentLoggedInUser) {
      name = t("common:you_with_full_name", { fullName: name });
    }

    const studentId = _.get(user, "sourceId", "");

    return (
      <UserTypeCard
        key={id}
        userId={id}
        mainText={name}
        subText={getSubText({
          user,
          t,
          defaultStudentNameSortPreference,
          showStudentId:
            showStudentId && type === STUDENT && !_.isEmpty(studentId),
          studentId,
        })}
        profileImage={profileImage}
        onRemoveClick={handleRemoveUser}
        testId={componentTestId}
        alertTypes={alertTypes}
        isStudentType={type === STUDENT}
      />
    );
  });
};

const UserTypeList = ({
  users,
  type,
  defaultStudentNameSortPreference,
  defaultStudentNameOrderPreference,
  loggedInUserType,
  loggedInUserId,
  onRemoveUsers,
  testId,
}) => {
  const [open, setOpen] = React.useState(true);

  const componentTestId = testId ? `${testId}-userTypeList` : undefined;
  const toggleAccordion = () => setOpen(open => !open);

  const { t } = useI18n(["common"]);

  if (_.isEmpty(users)) {
    return null;
  }
  const labelWithCount = `${t(userTypeLabelsPlural[type])} (${_.size(users)})`;

  const userTypeTitleClasses = classNames(
    classes.userTypeTitle,
    "text-label-3"
  );

  return (
    <div className={classes.userTypeList}>
      <button
        className={userTypeTitleClasses}
        onClick={toggleAccordion}
        data-test-id={`${componentTestId}-toggleAccordion-button`}
        type="button"
      >
        {labelWithCount}
        <IconButton
          dsVersion="2.0"
          variant="neutral"
          type="plain"
          size="medium"
          icon={<ChevronUpOutlined />}
          className={classNames(classes.accordion, {
            [classes.accordionClose]: !open,
          })}
        />
      </button>
      {open ? (
        <UserTypeCards
          users={users}
          onRemoveUsers={onRemoveUsers}
          defaultStudentNameSortPreference={defaultStudentNameSortPreference}
          defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
          loggedInUserType={loggedInUserType}
          loggedInUserId={loggedInUserId}
          testId={componentTestId}
        />
      ) : null}
    </div>
  );
};

const mapStateToProps = state => {
  const defaultStudentNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const { user_type: loggedInUserType, id: loggedInUserId } = getUserInfo();

  return {
    defaultStudentNameSortPreference,
    defaultStudentNameOrderPreference,
    loggedInUserType,
    loggedInUserId,
  };
};

const UserTypeListContainer = connect(mapStateToProps)(UserTypeList);

const ViewMembers = props => {
  const { users, onRemoveUsers, handleClearAll, testId } = props;
  const { t } = useI18n(["common"]);
  const {
    viewMembersEmptyStateTitle,
    viewMembersEmptyStateSubtitle,
    viewMembersEmptyStateIllustration,
    getRightPanelHeader,
    getRightPanelSubHeader,
    showRightPanelClearAll,
  } = useMetadata();
  const componentTestId = testId ? `${testId}-viewMembers` : undefined;
  const usersByType = React.useMemo(() => {
    return _.reduce(
      users,
      (acc, user) => {
        if (!acc[user.type]) {
          acc[user.type] = [];
        }
        acc[user.type].push(user);
        return acc;
      },
      {}
    );
  }, [users]);

  const headerClasses = classNames({
    [classes.flexBetween]: showRightPanelClearAll,
    "text-label": true,
  });

  const subtitleClasses = classNames("text-body-s", classes.headerSubtitle);

  return (
    <div className={classes.rootContainer}>
      <div className={classes.headerContainer}>
        <div className={headerClasses}>
          {getRightPanelHeader({
            t,
            users,
          })}
          {showRightPanelClearAll && !_.isEmpty(users) ? (
            <Button
              dsVersion="2.0"
              variant="progressive"
              type="plain"
              size="small"
              className={classes.clearAllButton}
              onClick={handleClearAll}
              testId={componentTestId}
            >
              {t("announcement:clear_all")}
            </Button>
          ) : null}
        </div>
        <div className={subtitleClasses}>
          {getRightPanelSubHeader({
            t,
            users,
          })}
        </div>
      </div>
      {_.isEmpty(users) ? (
        <EmptyState
          dsVersion="2.0"
          title={t(viewMembersEmptyStateTitle)}
          subtitle={t(viewMembersEmptyStateSubtitle)}
          illustration={viewMembersEmptyStateIllustration}
        />
      ) : (
        <div className={classes.membersContainer}>
          <UserTypeListContainer
            users={usersByType[STAFF]}
            type={STAFF}
            onRemoveUsers={onRemoveUsers}
            testId={componentTestId}
          />
          <UserTypeListContainer
            users={usersByType[STUDENT]}
            type={STUDENT}
            onRemoveUsers={onRemoveUsers}
            testId={componentTestId}
          />
          <UserTypeListContainer
            users={usersByType[PARENT]}
            type={PARENT}
            onRemoveUsers={onRemoveUsers}
            testId={componentTestId}
          />
        </div>
      )}
    </div>
  );
};

export default ViewMembers;
